import FirstRoundLetterComp from "components/FirstRoundLetter/FirstRoundLetter";
import BottomPagination from "components/Pagination/BottomPagination/BottomPagination";
import TopPagination from "components/Pagination/TopPagination/TopPagination";
import StatusWithDot from "components/StatusWithDot/StatusWithDot";
import TaskActionTooltip from "components/Tooltip/TaskActionTooltip";
import DeleteJobModal from "custom_modules/Jobs/DeleteJobModal";
import {
  IconButtonHolder,
  TaskStatusButton,
} from "custom_modules/Jobs/tasksStyles";
import { ApprovalBadge } from "custom_modules/TasksNew/tasksStyles";
import moment from "moment-timezone";
import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { GrayBox } from "styledComponents/buttons";

import avatar5 from "../../assets/img/avatar_5.svg";
import previewIconNormal from "../../assets/img/icons/Eye_normal.svg";
import deleteIconNormal from "../../assets/img/icons/delete_normal.svg";
import ComponentCircleLoader from "../../components/Loaders/ComponentCircleLoader";
import NoDataComponent from "../../components/NoDataComponent/NoDataComponent";
import AssigneeTooltip from "../../components/Tooltip/AssigneeTooltip";
import {
  ProjectTableContainerNew,
  TableItem,
  TableRowAndBudgetCollapseContainer,
} from "../../styledComponents/ProjectsNew";
import {
  CommonFlexIcon,
  CommonImage,
  CommonText,
  Container,
  TableText,
} from "../../styledComponents/common";
import {
  AssigneeRoundImage,
  AssigneeRoundImageSection,
  RoundPersonImage,
} from "../../styledComponents/createProject";
import { formatTimeInTimezone, globalDateFormat } from "../../utils/helper";

const WorkOrderJobTable = ({
  jobList,
  isLoading,
  history,
  totaljobsCount,
  itemName,
  currentPage,
  pageSize,
  onPageChange,
  selectedOrganization,
  deletingJobId,
  jobDeleteLoading,
  toggleDeleteModal,
}) => {
  const { t } = useTranslation();
  const [hoveredIndex, setHoveredIndex] = useState(-1);

  // Dynamic labels and paths based on organization type (default to "operation" if not defined)
  const orgType = selectedOrganization?.type || "operation";
  const isOperationType = orgType === "operation";
  const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
  const noItemFoundLabel = isOperationType
    ? t("no_tasks_found")
    : t("no_jobs_found");

  const itemNameLabel = isOperationType ? t("task_name") : t("job_name");
  const detailsPath = isOperationType
    ? "/user/task-details"
    : "/user/job-details";
  const isReadyToDispatchStatus = status =>
    status === "ready to dispatch" || status === "scheduled";

  if (isLoading || jobDeleteLoading) return <ComponentCircleLoader />;
  if (!isLoading && !jobDeleteLoading && jobList.length === 0)
    return <GrayBox>{`(${noItemFoundLabel})`}</GrayBox>;

  return (
    <div>
      {totaljobsCount && totaljobsCount > 0 && pageSize ? (
        <TopPagination
          itemName={itemName}
          totalCount={totaljobsCount}
          currentPage={currentPage}
          onPageChange={page => onPageChange(page)}
          pageSize={pageSize}
          margin="0 0 20px 0"
        />
      ) : null}

      <Container marginTop="5px" style={{ paddingBottom: "20px" }}>
        {/* Table Section */}
        <ProjectTableContainerNew heading>
          <TableItem>
            <TableText>{itemNameLabel}</TableText>
          </TableItem>
          <TableItem>
            <TableText>{t("assignee")}</TableText>
          </TableItem>
          <TableItem>
            <TableText>{t("date_time")}</TableText>
          </TableItem>
          <TableItem>
            <TableText>{t("status")}</TableText>
          </TableItem>
          <TableItem centerAlign>
            <TableText>{t("approval")}</TableText>
          </TableItem>
          <TableItem centerAlign>
            <TableText>{t("action")}</TableText>
          </TableItem>
        </ProjectTableContainerNew>

        {jobList.map(task => (
          <TableRowAndBudgetCollapseContainer key={task.id}>
            <ProjectTableContainerNew>
              {/* Job Name */}
              <TableItem>
                <TableText
                  hoverPointer
                  hoverUnderline
                  name
                  fontWeight="600"
                  onClick={() => {
                    const workOrderId =
                      typeof task.work_order === "object"
                        ? task.work_order?.id
                        : task.work_order;
                    if (workOrderId && task.id) {
                      history.push(
                        `${detailsPath}?id=${task.id}&${
                          orgType === "service" ? "job_id" : "task_id"
                        }=${workOrderId}`,
                      );
                    }
                  }}
                >
                  {task.name || "-"}
                </TableText>
              </TableItem>

              {/* Assignees */}
              <TableItem>
                <AssigneeRoundImageSection
                  position={task.assignees?.length || 0}
                  style={{ marginRight: "auto" }}
                >
                  {task.assignees && task.assignees.length > 0 ? (
                    task.assignees.map((person, index) => (
                      <Fragment key={person.id}>
                        <AssigneeRoundImage
                          index={index + 1}
                          onMouseEnter={() => setHoveredIndex(index)}
                          onMouseLeave={() => setHoveredIndex(-1)}
                        >
                          {person.avatar ? (
                            <RoundPersonImage
                              src={person.avatar || avatar5}
                              alt=""
                            />
                          ) : (
                            <FirstRoundLetterComp
                              text={`${person.first_name} ${person.last_name}`}
                              backColor={index}
                              color={person.color}
                            />
                          )}
                          {hoveredIndex === index && (
                            <AssigneeTooltip
                              toolTipText={`${person.first_name}${
                                person.last_name ? ` ${person.last_name}` : ""
                              }`}
                              top="-60px"
                              left="-55px"
                            />
                          )}
                        </AssigneeRoundImage>
                      </Fragment>
                    ))
                  ) : (
                    <CommonText>-</CommonText>
                  )}
                </AssigneeRoundImageSection>
              </TableItem>

              {/* Date/Time */}
              <TableItem>
                {task.start_time ? (
                  <TableText name>
                    {globalDateFormat(
                      moment(task.start_time)
                        .utc()
                        .format("YYYY-MM-DDTHH:mm:ss"),
                      orgTimezone,
                    )}{" "}
                    <br />
                    {formatTimeInTimezone(task.start_time, orgTimezone)} -{" "}
                    {formatTimeInTimezone(task.end_time, orgTimezone)}
                  </TableText>
                ) : (
                  <CommonText>-</CommonText>
                )}
              </TableItem>

              {/* Status */}
              <TableItem>
                <StatusWithDot status={task.status} />
                {/* <TaskStatusButton
                  status={task.status}
                  style={{ minWidth: "120px" }}
                >
                  {task.status === "unscheduled"
                    ? t("Unscheduled")
                    : task.status === "ready to dispatch"
                    ? t("Ready to dispatch")
                    : task.status === "completed"
                    ? t("completed")
                    : task.status === "partially complete"
                    ? t("partially_completed")
                    : task.status === "totally missed"
                    ? t("totally_missed")
                    : task.status === "missed"
                    ? t("missed")
                    : "-"}
                </TaskStatusButton> */}
              </TableItem>

              {/* Approval */}
              <TableItem centerAlign>
                {task.approval && task.approval.status ? (
                  <ApprovalBadge status={task.approval.status}>
                    {t(task.approval.status)}
                  </ApprovalBadge>
                ) : (
                  <CommonText>-</CommonText>
                )}
              </TableItem>
              <TableItem centerAlign>
                {selectedOrganization &&
                selectedOrganization.role &&
                (selectedOrganization.role === "admin" ||
                  selectedOrganization.role === "owner") &&
                task.status &&
                (task.status === "unscheduled" ||
                  isReadyToDispatchStatus(task.status) ||
                  task.status === "totally missed") ? (
                  <CommonFlexIcon gap="0px">
                    {/* Preview - Always shown for owner/admin */}
                    {/* <IconButtonHolder
                      onClick={() => {
                        const workOrderId =
                          typeof task.work_order === "object"
                            ? task.work_order?.id
                            : task.work_order;
                        if (workOrderId && task.id) {
                          history.push(
                            `${detailsPath}?id=${task.id}&${
                              orgType === "service" ? "task_id" : "job_id"
                            }=${workOrderId}`,
                          );
                        }
                      }}
                    >
                      <CommonImage src={previewIconNormal} alt="" size="24px" />
                      <TaskActionTooltip toolTipText={t("preview_job")} />
                    </IconButtonHolder> */}

                    {/* Delete - with status condition check */}
                    <IconButtonHolder
                      onClick={() => {
                        const workOrderId =
                          typeof task.work_order === "object"
                            ? task.work_order?.id
                            : task.work_order;
                        toggleDeleteModal(task.id, workOrderId);
                      }}
                    >
                      <CommonImage
                        src={deleteIconNormal}
                        alt="delete"
                        size="24px"
                      />
                      <TaskActionTooltip toolTipText={t("delete_job")} />
                    </IconButtonHolder>
                  </CommonFlexIcon>
                ) : (
                  <CommonText>-</CommonText>
                )}
              </TableItem>
            </ProjectTableContainerNew>
          </TableRowAndBudgetCollapseContainer>
        ))}
      </Container>
      {totaljobsCount && totaljobsCount > 0 && pageSize ? (
        <BottomPagination
          totalCount={totaljobsCount}
          currentPage={currentPage}
          onPageChange={page => onPageChange(page)}
          pageSize={pageSize}
        />
      ) : null}
    </div>
  );
};

export default WorkOrderJobTable;
